import { useState, useEffect, useCallback, useRef, startTransition } from 'react';
import { GroupBusinessReport } from '../../../service/incomeApi';
import {
  dataCacheManager,
  DimensionType,
  DimensionAnalysisResult,
  DetectionResult,
  DetectionStatus,
  analyzeAllDimensions,
} from './useAutoMonitor.utils';
import { generateMockStoresData } from './useAutoMonitor.mock';

/**
 * 自动监控参数接口
 */
export interface UseAutoMonitorParams {
  // 筛选条件
  regionIds?: number[];
  provinceIds?: number[];
  cityIds?: number[];
  brandIds?: number[];

  // 刷新配置
  enabled?: boolean; // 是否启用自动刷新
  refreshInterval?: number; // 刷新间隔（毫秒），默认1分钟

  // 批量处理配置
  batchSize?: number; // 每批处理的门店数量，默认100

  // 可见性过滤配置
  visibleStoreIds?: Set<number | string>; // 可见的门店ID集合，如果提供，只刷新可见的门店
  onlyRefreshVisible?: boolean; // 是否只刷新可见的门店，默认 true

  // 回调函数
  onDetection?: (results: DetectionResult[]) => void; // 检测结果回调
  onDimensionAnalysis?: (results: DimensionAnalysisResult[]) => void; // 维度分析结果回调
  onError?: (error: Error) => void; // 错误回调
}

/**
 * 自动监控结果接口
 */
export interface UseAutoMonitorResult {
  // 检测结果
  detectionResults: DetectionResult[];

  // 维度分析结果
  dimensionResults: DimensionAnalysisResult[];

  // 状态
  loading: boolean;
  error: Error | null;

  // 统计信息
  stats: {
    totalStores: number;
    checkedStores: number;
    totalDimensions: number;
    anomalies: number;
    warnings: number;
    errors: number;
    criticals: number;
  };

  // 控制方法
  start: () => void;
  stop: () => void;
  refresh: () => void;

  // 配置方法
  updateConfig: (config: Partial<UseAutoMonitorParams>) => void;
}

/**
 * 主Hook：自动监控所有门店数据
 * 按项检测策略：批量获取所有门店数据，然后对每个门店进行8个维度分析
 */
export function useAutoMonitor(params?: UseAutoMonitorParams): UseAutoMonitorResult {
  const {
    enabled = true,
    refreshInterval = 60 * 1000, // 默认1分钟
    batchSize = 100,
    visibleStoreIds,
    onlyRefreshVisible = true, // 默认只刷新可见的门店
    onDetection,
    onDimensionAnalysis,
    onError,
  } = params || {};

  // 状态管理
  const [detectionResults, setDetectionResults] = useState<DetectionResult[]>([]);
  const [dimensionResults, setDimensionResults] = useState<DimensionAnalysisResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // 使用 ref 跟踪是否已生成初始数据
  const hasInitialDataRef = useRef<boolean>(false);
  const [stats, setStats] = useState({
    totalStores: 0,
    checkedStores: 0,
    totalDimensions: 8,
    anomalies: 0,
    warnings: 0,
    errors: 0,
    criticals: 0,
  });

  // 控制状态
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * 批量获取门店数据（使用Mock数据）
   * 每分钟生成新的随机Mock数据
   */
  const fetchStoresData = useCallback(async (
    signal?: AbortSignal
  ): Promise<Array<{ storeId: number; storeName: string; data: GroupBusinessReport }>> => {
    try {
      if (signal?.aborted) {
        throw new Error('请求已取消');
      }

      // 只在开发环境输出日志
      if (process.env.NODE_ENV === 'development') {
        console.log('📤 [useAutoMonitor] 生成Mock门店数据...');
      }

      // 模拟API延迟：可被 signal 中断，切页时尽快结束
      if (signal) {
        const chunkMs = 50;
        const totalMs = 300;
        for (let elapsed = 0; elapsed < totalMs; elapsed += chunkMs) {
          if (signal.aborted) throw new Error('请求已取消');
          await new Promise((resolve) => setTimeout(resolve, Math.min(chunkMs, totalMs - elapsed)));
        }
      } else {
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      // 生成Mock数据（默认10个门店，可以根据筛选条件调整）
      const storeCount = 10; // 可以根据筛选条件调整数量
      const storesData = generateMockStoresData(storeCount);

      // 只在开发环境输出日志
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ [useAutoMonitor] 生成 ${storesData.length} 个门店的Mock数据`);
      }

      // 更新缓存
      dataCacheManager.updateStoresData(storesData);

      return storesData;
    } catch (err) {
      console.error('❌ [useAutoMonitor] 获取门店数据失败:', err);
      throw err;
    }
  }, []);

  /**
   * 对单个门店进行8个维度分析
   */
  const analyzeStore = useCallback((storeData: GroupBusinessReport): DimensionAnalysisResult[] => {
    try {
      // 获取历史数据（从缓存中获取，用于对比）
      const cached = dataCacheManager.getStoreData(storeData.hotelId);
      const historicalData = cached ? [cached.data] : undefined;

      // 执行8个维度分析
      const dimensionResults = analyzeAllDimensions(storeData, historicalData);

      // 更新缓存中的维度分析结果
      Object.values(dimensionResults).forEach((result) => {
        dataCacheManager.updateDimensionAnalysis(storeData.hotelId, result.dimension, result);
      });

      return Object.values(dimensionResults);
    } catch (err) {
      console.error(`❌ [useAutoMonitor] 分析门店 ${storeData.hotelId} 失败:`, err);
      return [];
    }
  }, []);

  /**
   * 从维度分析结果生成检测结果
   */
  const generateDetectionResults = useCallback((
    dimensionResults: DimensionAnalysisResult[]
  ): DetectionResult[] => {
    const results: DetectionResult[] = [];

    dimensionResults.forEach((dimResult) => {
      // 只生成异常状态的检测结果
      if (dimResult.status !== DetectionStatus.NORMAL) {
        results.push({
          itemId: `dimension-${dimResult.dimension}`,
          itemName: getDimensionName(dimResult.dimension),
          dimension: dimResult.dimension,
          storeId: dimResult.storeId,
          storeName: dimResult.storeName,
          status: dimResult.status,
          message: dimResult.message,
          indicators: dimResult.indicators,
          timestamp: dimResult.timestamp,
          suggestions: dimResult.suggestions,
        });
      }
    });

    return results;
  }, []);

  /**
   * 执行一次完整的检测流程
   */
  const performDetection = useCallback(async () => {
    if (!enabled) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (process.env.NODE_ENV === 'development' && !hasInitialDataRef.current) {
        console.log('🔄 [useAutoMonitor] 开始执行检测, 可见门店:', visibleStoreIds?.size ?? 0);
      }

      // 1. 获取所有门店数据（批量获取）
      // 注意：API会返回所有符合筛选条件的数据，如果数据量很大（如150个门店），
      // 可以考虑分批处理，但由于API不支持按门店ID列表查询，这里一次性获取所有数据
      const storesData = await fetchStoresData(abortControllerRef.current?.signal);

      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      // 只在开发环境输出
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ [useAutoMonitor] 获取到 ${storesData.length} 个门店的数据`);
      }

      // 2. 过滤可见的门店（如果启用了可见性过滤）
      let storesToProcess = storesData;
      if (onlyRefreshVisible && visibleStoreIds) {
        // 如果 visibleStoreIds 为空，说明没有可见的门店
        if (visibleStoreIds.size === 0) {
          // 如果还没有生成过初始数据，处理所有门店以生成初始数据
          if (!hasInitialDataRef.current) {
            // 只在开发环境输出日志
            if (process.env.NODE_ENV === 'development') {
              console.log('📊 [useAutoMonitor] 首次加载且没有可见门店，处理所有门店以生成初始统计卡片数据');
            }
            storesToProcess = storesData; // 处理所有门店
          } else {
            // 非首次加载且没有可见门店，跳过本次刷新
            // 只在开发环境输出日志
            if (process.env.NODE_ENV === 'development') {
              console.log('⏸️ [useAutoMonitor] 没有可见的门店，跳过本次刷新');
            }
            setLoading(false);
            return;
          }
        } else {
          // 有可见门店，只处理可见的门店
          storesToProcess = storesData.filter((item) => {
            const storeId = item.storeId;
            const isVisible = visibleStoreIds.has(storeId);
            if (!isVisible) {
              // 只在开发环境输出日志
            if (process.env.NODE_ENV === 'development') {
              console.log(`⏸️ [useAutoMonitor] 跳过不可见门店: ${storeId} (${item.storeName})`);
            }
            }
            return isVisible;
          });
          // 只在开发环境输出日志
          if (process.env.NODE_ENV === 'development') {
            console.log(
              `👁️ [useAutoMonitor] 可见性过滤: 总门店 ${storesData.length}，可见门店 ${storesToProcess.length}`
            );
          }
        }
      } else {
        // 未启用可见性过滤，处理所有门店（用于统计卡片）
        // 只在开发环境输出日志
        if (process.env.NODE_ENV === 'development') {
          console.log(`📊 [useAutoMonitor] 处理所有门店数据（${storesData.length}个），用于统计卡片`);
        }
      }

      // 3. 对每个门店进行8个维度分析
      // 如果门店数量超过batchSize，可以考虑分批处理，但分析是前端计算，可以一次性处理
      const allDimensionResults: DimensionAnalysisResult[] = [];
      const allDetectionResults: DetectionResult[] = [];

      // 如果门店数量很大，可以分批处理分析（虽然分析是前端计算，但可以避免阻塞UI）
      const totalStores = storesToProcess.length;
      const batches = Math.ceil(totalStores / batchSize);

      for (let batchIndex = 0; batchIndex < batches; batchIndex++) {
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        const startIndex = batchIndex * batchSize;
        const endIndex = Math.min(startIndex + batchSize, totalStores);
        const batch = storesToProcess.slice(startIndex, endIndex);

        // 只在开发环境或批次较多时输出日志
        if (process.env.NODE_ENV === 'development' && batches > 1) {
          console.log(`📊 [useAutoMonitor] 分析第 ${batchIndex + 1}/${batches} 批，门店数: ${batch.length}`);
        }

        // 批量分析
        for (const storeDataItem of batch) {
          if (abortControllerRef.current?.signal.aborted) {
            return;
          }

          // 分析单个门店
          const dimensionResults = analyzeStore(storeDataItem.data);
          allDimensionResults.push(...dimensionResults);

          // 生成检测结果
          const detectionResults = generateDetectionResults(dimensionResults);
          allDetectionResults.push(...detectionResults);
        }

        // 批次间让出主线程，避免阻塞菜单点击等用户操作
        if (batchIndex < batches - 1) {
          await new Promise((resolve) => setTimeout(resolve, 0));
        }

        // 每批分析完后，更新一次状态（可选，用于显示进度）；非紧急用 startTransition
        if (batchIndex < batches - 1) {
          startTransition(() => {
            setStats(prev => ({
              ...prev,
              checkedStores: endIndex,
              totalStores: totalStores,
            }));
          });
        }
      }

      // 切页后已 abort，不再 setState 避免 unmount 后更新
      if (abortControllerRef.current?.signal.aborted) return;

      // 4. 更新状态：非紧急，优先响应菜单点击
      startTransition(() => {
        setDimensionResults(allDimensionResults);
        setDetectionResults(allDetectionResults);
        if (allDimensionResults.length > 0) {
          hasInitialDataRef.current = true;
        }
        const newStats = {
          totalStores: storesData.length,
          checkedStores: storesToProcess.length,
          totalDimensions: 8,
          anomalies: allDetectionResults.length,
          warnings: allDetectionResults.filter(r => r.status === DetectionStatus.WARNING).length,
          errors: allDetectionResults.filter(r => r.status === DetectionStatus.ERROR).length,
          criticals: allDetectionResults.filter(r => r.status === DetectionStatus.CRITICAL).length,
        };
        setStats(newStats);
      });

      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ [useAutoMonitor] 检测完成，维度: ${allDimensionResults.length}，异常: ${allDetectionResults.length}`);
      }

      if (onDimensionAnalysis) {
        onDimensionAnalysis(allDimensionResults);
      }
      if (onDetection && allDetectionResults.length > 0) {
        onDetection(allDetectionResults);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('检测失败');
      console.error('❌ [useAutoMonitor] 检测失败:', error);
      setError(error);
      if (onError) {
        onError(error);
      }
    } finally {
      startTransition(() => setLoading(false));
    }
  }, [enabled, fetchStoresData, analyzeStore, generateDetectionResults, onDetection, onDimensionAnalysis, onError, batchSize, visibleStoreIds, onlyRefreshVisible]);

  /**
   * 启动监控
   */
  const start = useCallback(() => {
    if (isRunning) {
      console.warn('⚠️ [useAutoMonitor] 监控已在运行中');
      return;
    }

    // 只在开发环境输出日志
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 [useAutoMonitor] 启动监控');
    }
    setIsRunning(true);

    // 创建新的AbortController
    abortControllerRef.current = new AbortController();

    // 注意：不在这里立即执行，因为可能已经在useEffect中执行了
    // 设置定时刷新
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      performDetection();
    }, refreshInterval);
  }, [isRunning, refreshInterval, performDetection]);

  /**
   * 停止监控
   */
  const stop = useCallback(() => {
    // 只在开发环境输出日志
    if (process.env.NODE_ENV === 'development') {
      console.log('🛑 [useAutoMonitor] 停止监控');
    }
    setIsRunning(false);

    // 取消进行中的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // 清除定时器
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * 手动刷新
   */
  const refresh = useCallback(() => {
    // 只在开发环境输出日志
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 [useAutoMonitor] 手动刷新');
    }
    if (isRunning) {
      performDetection();
    } else {
      // 如果未运行，先启动
      start();
    }
  }, [isRunning, performDetection, start]);

  /**
   * 更新配置
   */
  const updateConfig = useCallback((config: Partial<UseAutoMonitorParams>) => {
    // 只在开发环境输出日志
    if (process.env.NODE_ENV === 'development') {
      console.log('⚙️ [useAutoMonitor] 更新配置:', config);
    }
    // 如果正在运行，需要重启以应用新配置
    const wasRunning = isRunning;
    if (wasRunning) {
      stop();
    }
    // 配置更新会在下次启动时生效
    // 这里可以添加配置更新逻辑
    if (wasRunning) {
      setTimeout(() => start(), 100);
    }
  }, [isRunning, stop, start]);

  // 组件挂载时延迟启动，避免首屏与大量分析同时执行导致 CPU 爆表
  useEffect(() => {
    if (!enabled) return;

    setIsRunning(true);
    abortControllerRef.current = new AbortController();

    // 延迟 3 秒再执行首次检测，让首屏先完成渲染
    const firstRunTimer = setTimeout(() => {
      performDetection();
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        performDetection();
      }, refreshInterval);
    }, 3000);

    return () => {
      clearTimeout(firstRunTimer);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在组件挂载时执行一次

  // 当enabled变化时，启动或停止
  useEffect(() => {
    if (enabled && !isRunning) {
      performDetection();
      start();
    } else if (!enabled && isRunning) {
      stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isRunning]);

  return {
    detectionResults,
    dimensionResults,
    loading,
    error,
    stats,
    start,
    stop,
    refresh,
    updateConfig,
  };
}

// ==================== 辅助函数 ====================

function getDimensionName(dimension: DimensionType): string {
  const names: Record<DimensionType, string> = {
    [DimensionType.REVENUE]: '营收维度',
    [DimensionType.OCCUPANCY]: '入住率维度',
    [DimensionType.PRICE]: '价格维度',
    [DimensionType.CUSTOMER_SOURCE]: '客源结构维度',
    [DimensionType.ROOM_STATUS]: '房态维度',
    [DimensionType.REVENUE_EFFICIENCY]: '收入效率维度',
    [DimensionType.TIME]: '时间维度',
    [DimensionType.OVERALL_HEALTH]: '综合健康度维度',
  };
  return names[dimension] || dimension;
}

// ==================== 待调价门店 ====================

/**
 * 待调价门店数据接口
 */
export interface PriceAdjustmentStore {
  id: number | string;
  name: string;
  hotelId?: number;
  hotelCode?: string;
  currentPrice?: number;
  suggestedPrice?: number;
  priceDifference?: number;
  [key: string]: any;
}

export interface UseAutoMonitorPriceAdjustmentStoresParams extends UseAutoMonitorParams { }
export interface UseAutoMonitorPriceAdjustmentStoresResult extends UseAutoMonitorResult<PriceAdjustmentStore> {
  stores: PriceAdjustmentStore[];
}

/**
 * Hook：自动监控待调价门店
 */
export function useAutoMonitorPriceAdjustmentStores(
  params?: UseAutoMonitorPriceAdjustmentStoresParams
): UseAutoMonitorPriceAdjustmentStoresResult {
  const [stores, setStores] = useState<PriceAdjustmentStore[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  const refreshKey = (params?.refreshKey || 0) + internalRefreshKey;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: 调用实际的 API 获取待调价门店数据
      await new Promise(resolve => setTimeout(resolve, 300));

      const mockStores: PriceAdjustmentStore[] = Array.from({ length: 3 }, (_, index) => ({
        id: `store-${index + 1}`,
        name: `待调价门店${index + 1}`,
        hotelId: index + 1,
        hotelCode: `HOTEL${String(index + 1).padStart(3, '0')}`,
        currentPrice: 300 + index * 50,
        suggestedPrice: 350 + index * 50,
        priceDifference: 50 + index * 10,
      }));

      setStores(mockStores);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('获取待调价门店数据失败'));
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const refresh = useCallback(() => {
    setInternalRefreshKey(prev => prev + 1);
  }, []);

  return {
    stores,
    count: stores.length,
    loading,
    error,
    refresh,
  };
}

// ==================== 价格异常 ====================

/**
 * 价格异常门店数据接口
 */
export interface PriceAnomalyStore {
  id: number | string;
  name: string;
  hotelId?: number;
  hotelCode?: string;
  anomalyType?: string;
  currentPrice?: number;
  expectedPrice?: number;
  deviation?: number;
  [key: string]: any;
}

export interface UseAutoMonitorPriceAnomalyParams extends UseAutoMonitorParams { }
export interface UseAutoMonitorPriceAnomalyResult extends UseAutoMonitorResult<PriceAnomalyStore> {
  stores: PriceAnomalyStore[];
}

/**
 * Hook：自动监控价格异常
 */
export function useAutoMonitorPriceAnomaly(
  params?: UseAutoMonitorPriceAnomalyParams
): UseAutoMonitorPriceAnomalyResult {
  const [stores, setStores] = useState<PriceAnomalyStore[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  const refreshKey = (params?.refreshKey || 0) + internalRefreshKey;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: 调用实际的 API 获取价格异常门店数据
      await new Promise(resolve => setTimeout(resolve, 300));

      const mockStores: PriceAnomalyStore[] = Array.from({ length: 5 }, (_, index) => ({
        id: `anomaly-${index + 1}`,
        name: `价格异常门店${index + 1}`,
        hotelId: index + 1,
        hotelCode: `HOTEL${String(index + 1).padStart(3, '0')}`,
        anomalyType: ['过高', '过低', '波动大'][index % 3],
        currentPrice: 400 + index * 30,
        expectedPrice: 350 + index * 20,
        deviation: 50 + index * 10,
      }));

      setStores(mockStores);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('获取价格异常数据失败'));
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const refresh = useCallback(() => {
    setInternalRefreshKey(prev => prev + 1);
  }, []);

  return {
    stores,
    count: stores.length,
    loading,
    error,
    refresh,
  };
}

// ==================== 满房 ====================

/**
 * 满房门店数据接口
 */
export interface FullyBookedStore {
  id: number | string;
  name: string;
  hotelId?: number;
  hotelCode?: string;
  roomTotal?: number;
  roomOccupied?: number;
  occupancyRate?: number;
  [key: string]: any;
}

export interface UseAutoMonitorFullyBookedParams extends UseAutoMonitorParams { }
export interface UseAutoMonitorFullyBookedResult extends UseAutoMonitorResult<FullyBookedStore> {
  stores: FullyBookedStore[];
}

/**
 * Hook：自动监控满房
 */
export function useAutoMonitorFullyBooked(
  params?: UseAutoMonitorFullyBookedParams
): UseAutoMonitorFullyBookedResult {
  const [stores, setStores] = useState<FullyBookedStore[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  const refreshKey = (params?.refreshKey || 0) + internalRefreshKey;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: 调用实际的 API 获取满房门店数据
      await new Promise(resolve => setTimeout(resolve, 300));

      const mockStores: FullyBookedStore[] = Array.from({ length: 8 }, (_, index) => ({
        id: `booked-${index + 1}`,
        name: `满房门店${index + 1}`,
        hotelId: index + 1,
        hotelCode: `HOTEL${String(index + 1).padStart(3, '0')}`,
        roomTotal: 50 + index * 10,
        roomOccupied: 50 + index * 10,
        occupancyRate: 100,
      }));

      setStores(mockStores);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('获取满房数据失败'));
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const refresh = useCallback(() => {
    setInternalRefreshKey(prev => prev + 1);
  }, []);

  return {
    stores,
    count: stores.length,
    loading,
    error,
    refresh,
  };
}

// ==================== 客源异常 ====================

/**
 * 客源异常门店数据接口
 */
export interface CustomerSourceAnomalyStore {
  id: number | string;
  name: string;
  hotelId?: number;
  hotelCode?: string;
  anomalyType?: string;
  normalChannelRatio?: number;
  currentChannelRatio?: number;
  [key: string]: any;
}

export interface UseAutoMonitorCustomerSourceAnomalyParams extends UseAutoMonitorParams { }
export interface UseAutoMonitorCustomerSourceAnomalyResult extends UseAutoMonitorResult<CustomerSourceAnomalyStore> {
  stores: CustomerSourceAnomalyStore[];
}

/**
 * Hook：自动监控客源异常
 */
export function useAutoMonitorCustomerSourceAnomaly(
  params?: UseAutoMonitorCustomerSourceAnomalyParams
): UseAutoMonitorCustomerSourceAnomalyResult {
  const [stores, setStores] = useState<CustomerSourceAnomalyStore[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  const refreshKey = (params?.refreshKey || 0) + internalRefreshKey;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: 调用实际的 API 获取客源异常门店数据
      await new Promise(resolve => setTimeout(resolve, 300));

      const mockStores: CustomerSourceAnomalyStore[] = Array.from({ length: 11 }, (_, index) => ({
        id: `customer-${index + 1}`,
        name: `客源异常门店${index + 1}`,
        hotelId: index + 1,
        hotelCode: `HOTEL${String(index + 1).padStart(3, '0')}`,
        anomalyType: ['渠道异常', '客源结构异常', '流量异常'][index % 3],
        normalChannelRatio: 0.3 + (index % 3) * 0.1,
        currentChannelRatio: 0.5 + (index % 3) * 0.15,
      }));

      setStores(mockStores);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('获取客源异常数据失败'));
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const refresh = useCallback(() => {
    setInternalRefreshKey(prev => prev + 1);
  }, []);

  return {
    stores,
    count: stores.length,
    loading,
    error,
    refresh,
  };
}

// ==================== 停售异常 ====================

/**
 * 停售异常门店数据接口
 */
export interface SalesStoppedAnomalyStore {
  id: number | string;
  name: string;
  hotelId?: number;
  hotelCode?: string;
  stoppedRoomTypes?: string[];
  stoppedDuration?: number;
  reason?: string;
  [key: string]: any;
}

export interface UseAutoMonitorSalesStoppedAnomalyParams extends UseAutoMonitorParams { }
export interface UseAutoMonitorSalesStoppedAnomalyResult extends UseAutoMonitorResult<SalesStoppedAnomalyStore> {
  stores: SalesStoppedAnomalyStore[];
}

/**
 * Hook：自动监控停售异常
 */
export function useAutoMonitorSalesStoppedAnomaly(
  params?: UseAutoMonitorSalesStoppedAnomalyParams
): UseAutoMonitorSalesStoppedAnomalyResult {
  const [stores, setStores] = useState<SalesStoppedAnomalyStore[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  const refreshKey = (params?.refreshKey || 0) + internalRefreshKey;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: 调用实际的 API 获取停售异常门店数据
      await new Promise(resolve => setTimeout(resolve, 300));

      const mockStores: SalesStoppedAnomalyStore[] = Array.from({ length: 3 }, (_, index) => ({
        id: `stopped-${index + 1}`,
        name: `停售异常门店${index + 1}`,
        hotelId: index + 1,
        hotelCode: `HOTEL${String(index + 1).padStart(3, '0')}`,
        stoppedRoomTypes: ['标准间', '豪华间', '套房'].slice(0, index + 1),
        stoppedDuration: 24 + index * 12,
        reason: ['维护中', '库存不足', '价格调整'][index],
      }));

      setStores(mockStores);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('获取停售异常数据失败'));
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const refresh = useCallback(() => {
    setInternalRefreshKey(prev => prev + 1);
  }, []);

  return {
    stores,
    count: stores.length,
    loading,
    error,
    refresh,
  };
}

// ==================== 评价异常 ====================

/**
 * 评价异常门店数据接口
 */
export interface ReviewAnomalyStore {
  id: number | string;
  name: string;
  hotelId?: number;
  hotelCode?: string;
  averageRating?: number;
  reviewCount?: number;
  anomalyType?: string;
  [key: string]: any;
}

export interface UseAutoMonitorReviewAnomalyParams extends UseAutoMonitorParams { }
export interface UseAutoMonitorReviewAnomalyResult extends UseAutoMonitorResult<ReviewAnomalyStore> {
  stores: ReviewAnomalyStore[];
}

/**
 * Hook：自动监控评价异常
 */
export function useAutoMonitorReviewAnomaly(
  params?: UseAutoMonitorReviewAnomalyParams
): UseAutoMonitorReviewAnomalyResult {
  const [stores, setStores] = useState<ReviewAnomalyStore[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  const refreshKey = (params?.refreshKey || 0) + internalRefreshKey;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: 调用实际的 API 获取评价异常门店数据
      await new Promise(resolve => setTimeout(resolve, 300));

      const mockStores: ReviewAnomalyStore[] = Array.from({ length: 21 }, (_, index) => ({
        id: `review-${index + 1}`,
        name: `评价异常门店${index + 1}`,
        hotelId: index + 1,
        hotelCode: `HOTEL${String(index + 1).padStart(3, '0')}`,
        averageRating: 3.0 + (index % 3) * 0.5,
        reviewCount: 10 + index * 5,
        anomalyType: ['评分过低', '差评过多', '评价异常'][index % 3],
      }));

      setStores(mockStores);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('获取评价异常数据失败'));
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const refresh = useCallback(() => {
    setInternalRefreshKey(prev => prev + 1);
  }, []);

  return {
    stores,
    count: stores.length,
    loading,
    error,
    refresh,
  };
}

// ==================== 大事件地区 ====================

/**
 * 大事件地区数据接口
 */
export interface MajorEventArea {
  id: number | string;
  name: string;
  regionId?: number;
  regionName?: string;
  eventType?: string;
  eventDate?: string;
  affectedStores?: number;
  [key: string]: any;
}

export interface UseAutoMonitorMajorEventAreasParams extends UseAutoMonitorParams { }
export interface UseAutoMonitorMajorEventAreasResult extends UseAutoMonitorResult<MajorEventArea> {
  areas: MajorEventArea[];
}

/**
 * Hook：自动监控大事件地区
 */
export function useAutoMonitorMajorEventAreas(
  params?: UseAutoMonitorMajorEventAreasParams
): UseAutoMonitorMajorEventAreasResult {
  const [areas, setAreas] = useState<MajorEventArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  const refreshKey = (params?.refreshKey || 0) + internalRefreshKey;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: 调用实际的 API 获取大事件地区数据
      await new Promise(resolve => setTimeout(resolve, 300));

      const mockAreas: MajorEventArea[] = Array.from({ length: 13 }, (_, index) => ({
        id: `area-${index + 1}`,
        name: `大事件地区${index + 1}`,
        regionId: index + 1,
        regionName: `区域${index + 1}`,
        eventType: ['展会', '会议', '节日', '体育赛事'][index % 4],
        eventDate: new Date(Date.now() + index * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        affectedStores: 5 + index * 2,
      }));

      setAreas(mockAreas);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('获取大事件地区数据失败'));
      setAreas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const refresh = useCallback(() => {
    setInternalRefreshKey(prev => prev + 1);
  }, []);

  return {
    areas,
    count: areas.length,
    loading,
    error,
    refresh,
  };
}

// ==================== 出租率异常 ====================

/**
 * 出租率异常门店数据接口
 */
export interface OccupancyRateAnomalyStore {
  id: number | string;
  name: string;
  hotelId?: number;
  hotelCode?: string;
  occupancyRate?: number;
  expectedOccupancyRate?: number;
  deviation?: number;
  anomalyType?: string;
  [key: string]: any;
}

export interface UseAutoMonitorOccupancyRateAnomalyParams extends UseAutoMonitorParams { }
export interface UseAutoMonitorOccupancyRateAnomalyResult extends UseAutoMonitorResult<OccupancyRateAnomalyStore> {
  stores: OccupancyRateAnomalyStore[];
}

/**
 * Hook：自动监控出租率异常
 */
export function useAutoMonitorOccupancyRateAnomaly(
  params?: UseAutoMonitorOccupancyRateAnomalyParams
): UseAutoMonitorOccupancyRateAnomalyResult {
  const [stores, setStores] = useState<OccupancyRateAnomalyStore[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  const refreshKey = (params?.refreshKey || 0) + internalRefreshKey;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: 调用实际的 API 获取出租率异常门店数据
      await new Promise(resolve => setTimeout(resolve, 300));

      const mockStores: OccupancyRateAnomalyStore[] = Array.from({ length: 4 }, (_, index) => ({
        id: `occupancy-${index + 1}`,
        name: `出租率异常门店${index + 1}`,
        hotelId: index + 1,
        hotelCode: `HOTEL${String(index + 1).padStart(3, '0')}`,
        occupancyRate: 30 + index * 10,
        expectedOccupancyRate: 70 + index * 5,
        deviation: 40 + index * 5,
        anomalyType: ['过低', '过高', '波动大', '异常'][index],
      }));

      setStores(mockStores);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('获取出租率异常数据失败'));
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const refresh = useCallback(() => {
    setInternalRefreshKey(prev => prev + 1);
  }, []);

  return {
    stores,
    count: stores.length,
    loading,
    error,
    refresh,
  };
}
